import { useEffect, useMemo, useState } from 'react'
import { Modal } from '../../componentes/Modal'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import {
  useCatalogoGE,
  useConfigGE,
  useFijarRecurrencia,
  useSugerenciaRecurrencia,
  type FilaGE,
  type LineaPedidoGE,
} from '../../hooks/useGrandesEmpresas'

/**
 * Modal "Configurar recurrencia" de un GE (opción A, aprobada por el usuario):
 * cadencia con presets rápidos (7/14/28) + campo "otro" para cualquier número,
 * día de reparto en segmented L-V, y pedido fijo (catálogo sin bonos + cantidades)
 * con total en vivo. Lo que se guarde aquí lo usa el cron diario para generar el
 * albarán automático (B.10).
 */

const PRESETS = [7, 14, 28] as const
const DIAS_SEMANA = [
  { v: 0, txt: 'Lun' },
  { v: 1, txt: 'Mar' },
  { v: 2, txt: 'Mié' },
  { v: 3, txt: 'Jue' },
  { v: 4, txt: 'Vie' },
] as const

export function ModalRecurrenciaGE({ ge, onCerrar }: { ge: FilaGE; onCerrar: () => void }) {
  const config = useConfigGE(ge.contactId)
  const catalogo = useCatalogoGE(true)
  const fijar = useFijarRecurrencia()
  const [verSugerencia, setVerSugerencia] = useState(false)
  const sugerencia = useSugerenciaRecurrencia(verSugerencia ? ge.contactId : null)

  const [dias, setDias] = useState<number | null>(ge.recurrenciaDias ?? null)
  const [diaReparto, setDiaReparto] = useState<number | null>(null)
  const [cantidades, setCantidades] = useState<Record<string, number>>({})
  const [busqueda, setBusqueda] = useState('')
  const [cargadoInicial, setCargadoInicial] = useState(false)

  // Al llegar la config del backend, rellena los campos (una vez).
  useEffect(() => {
    if (config.data && !cargadoInicial) {
      setDias(config.data.recurrenciaDias)
      setDiaReparto(config.data.diaReparto)
      const base: Record<string, number> = {}
      for (const l of config.data.pedidoFijo ?? []) {
        base[String(l.product_id ?? l.nombre)] = l.cantidad
      }
      setCantidades(base)
      setCargadoInicial(true)
    }
  }, [config.data, cargadoInicial])

  const productos = catalogo.data?.items ?? []
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return q ? productos.filter((p) => p.nombre.toLowerCase().includes(q)) : productos
  }, [productos, busqueda])

  const lineas: LineaPedidoGE[] = useMemo(
    () =>
      productos
        .filter((p) => (cantidades[String(p.id)] ?? 0) > 0)
        .map((p) => ({ nombre: p.nombre, cantidad: cantidades[String(p.id)], precio: p.precio, product_id: p.id })),
    [productos, cantidades],
  )

  const totalConIva = useMemo(
    () => productos.reduce((s, p) => s + (cantidades[String(p.id)] ?? 0) * p.precioConIva, 0),
    [productos, cantidades],
  )

  const esPreset = dias !== null && (PRESETS as readonly number[]).includes(dias)

  function setCantidad(id: string, n: number) {
    setCantidades((prev) => ({ ...prev, [id]: Math.max(0, n) }))
  }

  function guardar() {
    fijar.mutate(
      { contactId: ge.contactId, dias, pedidoFijo: lineas.length ? lineas : null, diaReparto },
      { onSuccess: () => onCerrar() },
    )
  }

  function quitar() {
    fijar.mutate(
      { contactId: ge.contactId, dias: null, pedidoFijo: null, diaReparto: null },
      { onSuccess: () => onCerrar() },
    )
  }

  return (
    <Modal titulo={`Recurrencia · ${ge.nombre}`} onCerrar={onCerrar} anchoMax="max-w-3xl">
      <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-tinta">Configurar recurrencia</h3>
          <p className="mt-0.5 text-xs text-tinta-apagada">
            {ge.nombre} · CIF {ge.cif || '—'} · El albarán se generará solo cada N días con este pedido.
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
        >
          ✕
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
        {/* Cadencia */}
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Cada cuántos días</p>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDias(d)}
                aria-pressed={dias === d}
                className={[
                  'rounded-full border px-3.5 py-1.5 text-sm font-semibold transition',
                  dias === d ? 'border-acento bg-acento text-tinta-inversa' : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                ].join(' ')}
              >
                {d} días
              </button>
            ))}
            <input
              type="number"
              min={1}
              placeholder="Otro (días)"
              value={!esPreset && dias !== null ? dias : ''}
              onChange={(e) => {
                const n = Number(e.target.value)
                setDias(Number.isInteger(n) && n > 0 ? n : null)
              }}
              className="w-32 rounded-lg border border-borde-fuerte bg-superficie px-3 py-1.5 text-sm text-tinta placeholder:text-tinta-apagada"
            />
            {!verSugerencia ? (
              <button
                type="button"
                onClick={() => setVerSugerencia(true)}
                className="text-xs font-medium text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
              >
                ¿Cuál le toca?
              </button>
            ) : sugerencia.isPending ? (
              <span className="text-xs text-tinta-apagada">calculando…</span>
            ) : sugerencia.data?.dias ? (
              <button
                type="button"
                onClick={() => setDias(sugerencia.data!.dias)}
                className="rounded-full border border-acento-borde bg-acento-suave px-3 py-1 text-xs font-semibold text-acento-tinta hover:brightness-110"
                title={`Pide cada ~${sugerencia.data.intervaloMedio} días de media (${sugerencia.data.muestras} repartos)`}
              >
                Sugerido: {sugerencia.data.dias}d
              </button>
            ) : (
              <span className="text-xs text-tinta-apagada">sin datos para sugerir</span>
            )}
          </div>
          {dias ? <p className="mt-1.5 text-xs text-tinta-apagada">Cada {dias} días se generará el albarán automáticamente.</p> : null}
        </div>

        {/* Día de reparto */}
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Día de reparto</p>
          <div className="inline-flex overflow-hidden rounded-lg border border-borde-fuerte">
            {DIAS_SEMANA.map((d, i) => (
              <button
                key={d.v}
                type="button"
                onClick={() => setDiaReparto(diaReparto === d.v ? null : d.v)}
                aria-pressed={diaReparto === d.v}
                className={[
                  'px-3.5 py-1.5 text-sm font-semibold transition-colors',
                  i > 0 ? 'border-l border-borde-fuerte' : '',
                  diaReparto === d.v ? 'bg-acento text-tinta-inversa' : 'bg-superficie text-tinta-2 hover:bg-elevado',
                ].join(' ')}
              >
                {d.txt}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-tinta-apagada">Opcional: encaja el albarán en ese día de la semana.</p>
        </div>

        {/* Pedido fijo */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">Pedido fijo (se repite cada ciclo)</p>
            <input
              type="search"
              placeholder="Buscar producto…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-44 rounded-md border border-borde-fuerte bg-superficie px-3 py-1 text-xs text-tinta placeholder:text-tinta-apagada"
            />
          </div>
          {catalogo.isPending ? (
            <Cargando mensaje="Cargando catálogo" />
          ) : catalogo.isError ? (
            <EstadoError error={catalogo.error} onReintentar={() => void catalogo.refetch()} />
          ) : (
            <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
              {filtrados.map((p) => {
                const id = String(p.id)
                const q = cantidades[id] ?? 0
                return (
                  <li
                    key={id}
                    className={[
                      'flex items-center gap-3 rounded-lg border px-3 py-2',
                      q > 0 ? 'border-acento bg-acento-suave' : 'border-borde-suave bg-lienzo',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-tinta">{p.nombre}</p>
                      <p className="text-xs text-tinta-apagada">{p.precioConIva.toFixed(2)} € / ud (IVA incl.)</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" aria-label={`Quitar ${p.nombre}`} onClick={() => setCantidad(id, q - 1)} className="size-7 rounded-md border border-borde-fuerte text-tinta-2 hover:bg-elevado">−</button>
                      <input
                        type="number"
                        min={0}
                        value={q}
                        onChange={(e) => setCantidad(id, Number(e.target.value))}
                        className="w-14 rounded-md border border-borde-fuerte bg-superficie px-2 py-1 text-center text-sm text-tinta tabular-nums"
                      />
                      <button type="button" aria-label={`Añadir ${p.nombre}`} onClick={() => setCantidad(id, q + 1)} className="size-7 rounded-md border border-borde-fuerte text-tinta-2 hover:bg-elevado">+</button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Pie: resumen + acciones */}
      <div className="flex items-center justify-between gap-3 border-t border-borde px-5 py-4">
        <div className="text-sm text-tinta-2">
          {lineas.length === 0 ? (
            'Sin pedido fijo'
          ) : (
            <>
              <span className="font-semibold text-tinta">{totalConIva.toFixed(2)} €</span> / ciclo ·{' '}
              <span className="font-semibold text-tinta">{lineas.length}</span> producto{lineas.length === 1 ? '' : 's'}
            </>
          )}
        </div>
        <div className="flex gap-2">
          {config.data?.recurrenciaDias ? (
            <button
              type="button"
              onClick={quitar}
              disabled={fijar.isPending}
              className="rounded-lg border border-borde-fuerte bg-superficie px-4 py-2 text-sm font-medium text-tinta-2 hover:bg-elevado disabled:opacity-50"
            >
              Quitar recurrencia
            </button>
          ) : null}
          <button
            type="button"
            onClick={guardar}
            disabled={fijar.isPending || !dias || lineas.length === 0}
            title={!dias ? 'Fija una cadencia' : lineas.length === 0 ? 'Añade al menos un producto al pedido fijo' : ''}
            className="rounded-lg bg-acento px-4 py-2 text-sm font-medium text-tinta-inversa hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {fijar.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {fijar.isError ? (
        <p role="alert" className="px-5 pb-4 text-sm text-critico-tinta">No se pudo guardar la recurrencia. Inténtalo de nuevo.</p>
      ) : null}
    </Modal>
  )
}
